import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    ICustomerWithCart,
    ICustomerDetail,
    ISetCustomerData,
    IAddItemBase,
    ICartItemData,
    IBookingCart,
    IUpdateCartItemData,
    IActiveDiscountDetail,
} from "@interfaces";
import { CartConstants } from "@constants";
import { cartApi } from "@api";
import { InitStages, LoadingStage } from "src/Interfaces/shared";
import { getItemsPriceInfo, updateItems } from "./checkoutHelpers";
import { RemovePayloadCart } from "@utilities";
import { discountApi } from "src/Api/discountApi";
import { isNil } from "lodash";
import { store } from "@redux";

// Function to update totalPrice
const setTotalPrice = (state: IBookingCart) => {
    const priceInfo = getItemsPriceInfo(state.cartItems);
    state.totalRRP = priceInfo.totalRRP;
    state.totalNet = priceInfo.totalNetRate;
    state.commission = priceInfo.commission;
    state.commissionPercent = priceInfo.commissionPercent;
    state.totalPrice = priceInfo.totalPrice;
};

const initialState: IBookingCart = {
    cartItems: [],
    cartItemsLoadingStage: LoadingStage.NOT_INITIALIZED,
    totalRRP: 0,
    totalNet: 0,
    totalPrice: 0,
    commission: 0,
    commissionPercent: 0,
    customers: [],
    customersLoadingStage: LoadingStage.NOT_INITIALIZED,
    isShare: false,
    optionalFieldValidated: true,
    canCheckout: false,
    redirectUrl: "",
    bookingReference: "",
    step: CartConstants.CART,
    currentStep: CartConstants.CART,
};

export const INCREASE_QTY = "increase";
export const DECREASE_QTY = "decrease";

export const fetchActiveDiscount = createAsyncThunk<IActiveDiscountDetail>(
    "booking/fetchActiveDiscount",
    async (_, { rejectWithValue }) => {
        try {
            const state = store.getState();
            const isAuthenticated = !isNil(state.auth.jwt) && !isNil(state.account.account);
            const response = await discountApi.getActiveDiscount(isAuthenticated);
            return response.data.data;
        } catch (error) {
            console.error("Failed to get active discount:", error);
            return rejectWithValue("Unable to load active discount.");
        }
    },
);

export const fetchCartItems = createAsyncThunk<ICartItemData[]>(
    "booking/fetchCartItems",
    async (_, { rejectWithValue }) => {
        try {
            const response = await cartApi.getCart();
            return response.data.data as ICartItemData[];
        } catch (error) {
            console.error("Failed to fetch the cart:", error);
            return rejectWithValue("Unable to fetch cart data.");
        }
    },
);

export const addToCartAndFetch = createAsyncThunk<ICartItemData[], IAddItemBase>(
    "booking/addToCartAndFetch",
    async (newCartItem, { rejectWithValue }) => {
        try {
            await cartApi.addCart(newCartItem);
            const response = await cartApi.getCart();
            return response.data.data as ICartItemData[];
        } catch (error) {
            console.error("Failed to add item to cart or fetch updated cart:", error);
            return rejectWithValue("Unable to add item to cart and fetch updated cart data.");
        }
    },
);

export const fetchCustomers = createAsyncThunk<ICustomerWithCart[]>(
    "booking/fetchCustomers",
    async (_, { rejectWithValue }) => {
        try {
            const response = await cartApi.getCustomer();
            return response.data.data as ICustomerWithCart[];
        } catch (error) {
            console.error("Failed to fetch customers:", error);
            return rejectWithValue("Unable to fetch customer data.");
        }
    },
);

export const updateAndFetchCustomers = createAsyncThunk<ICustomerDetail[], ISetCustomerData[]>(
    "booking/updateAndFetchCustomers",
    async (newCartItem, { rejectWithValue }) => {
        try {
            await cartApi.updateCustomer(newCartItem);
            const response = await cartApi.getCustomer();
            return response.data.data as ICustomerWithCart[];
        } catch (error) {
            console.error("Failed to update customer or fetch customer data:", error);
            return rejectWithValue("Unable to update customer and fetch updated customer data.");
        }
    },
);

export const submitCartDataAndFetch = createAsyncThunk<any, IUpdateCartItemData[]>(
    "booking/submitCartDataAndFetch",
    async (itemsBookingData, { rejectWithValue }) => {
        try {
            const responseSubmit = await updateItems(itemsBookingData, "cart");
            if (responseSubmit.success === true && responseSubmit.code === 200) {
                const response = await cartApi.getCart();
                return response.data.data as ICartItemData[];
            } else {
                throw new Error("Cart update failed");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);
export const removeCartItemAndFetch = createAsyncThunk<ICartItemData[], RemovePayloadCart>(
    "booking/removeCartAndFetch",
    async (payload, { rejectWithValue }) => {
        try {
            await cartApi.removeCartItem(payload);
            const response = await cartApi.getCart();
            return response.data.data as ICartItemData[];
        } catch (error) {
            console.error("Failed to remove/fetch cart:", error);
            return rejectWithValue("Unable to update cart after removal.");
        }
    },
);

const checkout = createSlice({
    name: "bookingCart",
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCartItems.pending, (state) => {
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.IS_INITIALIZING
                    : LoadingStage.IS_LOADING;
            })
            .addCase(fetchCartItems.fulfilled, (state, action) => {
                try {
                    state.cartItems = action.payload;
                    setTotalPrice(state);
                    state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                        ? LoadingStage.INITIALIZE_COMPLETE
                        : LoadingStage.LOADING_COMPLETE;
                    setTotalPrice(state);
                } catch (error) {
                    console.error("Failed to update cart:", error);
                    state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                        ? LoadingStage.INITIALIZE_FAILURE
                        : LoadingStage.LOADING_FAILURE;
                }
            })
            .addCase(fetchCartItems.rejected, (state, action) => {
                console.error("Failed to fetch cart:", action.payload);
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.INITIALIZE_FAILURE
                    : LoadingStage.LOADING_FAILURE;
            })
            .addCase(fetchCustomers.pending, (state) => {
                state.customersLoadingStage = InitStages.includes(state.customersLoadingStage)
                    ? LoadingStage.IS_INITIALIZING
                    : LoadingStage.IS_LOADING;
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.customers = action.payload;
                state.customersLoadingStage = InitStages.includes(state.customersLoadingStage)
                    ? LoadingStage.INITIALIZE_COMPLETE
                    : LoadingStage.LOADING_COMPLETE;
            })
            .addCase(fetchCustomers.rejected, (state) => {
                state.customersLoadingStage = InitStages.includes(state.customersLoadingStage)
                    ? LoadingStage.INITIALIZE_FAILURE
                    : LoadingStage.LOADING_FAILURE;
            })
            .addCase(updateAndFetchCustomers.fulfilled, (state, action) => {
                state.customers = action.payload;
                state.customersLoadingStage = LoadingStage.LOADING_COMPLETE;
            })
            .addCase(updateAndFetchCustomers.rejected, (state) => {
                state.customersLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(addToCartAndFetch.fulfilled, (state, action) => {
                try {
                    state.cartItems = action.payload;
                    setTotalPrice(state);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_COMPLETE;
                } catch (error) {
                    console.error("Failed to update cart:", error);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
                }
            })
            .addCase(addToCartAndFetch.pending, (state) => {
                state.cartItemsLoadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(addToCartAndFetch.rejected, (state, action) => {
                console.error("Failed to update cart:", action.payload);
                state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(submitCartDataAndFetch.rejected, (state, action) => {
                console.error("Failed to submit cart data and fetch", action.payload);
                state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(submitCartDataAndFetch.fulfilled, (state, action) => {
                try {
                    state.cartItems = action.payload;
                    setTotalPrice(state);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_COMPLETE;
                } catch (error) {
                    console.error("Failed to update cart:", error);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
                }
            })
            .addCase(removeCartItemAndFetch.fulfilled, (state, action) => {
                state.cartItems = action.payload;
                setTotalPrice(state);
                state.cartItemsLoadingStage = LoadingStage.LOADING_COMPLETE;
            })
            .addCase(removeCartItemAndFetch.pending, (state) => {
                state.cartItemsLoadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(removeCartItemAndFetch.rejected, (state, action) => {
                console.error("Failed to remove item from cart:", action.payload);
                state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(fetchActiveDiscount.fulfilled, (state, action) => {
                state.activeDiscount = action.payload;
            })
            .addCase(fetchActiveDiscount.rejected, (_state, action) => {
                console.error("Failed to fetch active discount:", action.payload);
            });
    },
});

export const checkoutReducer = checkout.reducer;
